/** Past filings: every filing that has been issued, newest first - ADR-0212 decision 6 and the build
 * plan's Filings / Past filings tab.
 *
 * Each row is an issued artifact (ADR-0020): frozen, hashed and on the ledger. Its fingerprint is the
 * artifact's own SHA-256 and its page is the artifact's own view; nothing here re-derives a figure.
 * "Issued" means a filing exists in EmitIQ. It does not mean anything was sent to a regulator, and
 * no word here says so.
 *
 * One read per obligation (`useQueries`, the same key as `useArtifacts`, so a list the filing page
 * already holds is not read twice). A read that failed is shown as failed and the count is not
 * printed: a zero over a failed read would say nothing was ever issued.
 */

import { useQueries } from "@tanstack/react-query";
import { Archive } from "@phosphor-icons/react";

import { api } from "../../api/client";
import type { Artifact, Obligation } from "../../api/queries";
import { apiUrl } from "../../api/client";
import { refusalFrom } from "../../api/refusal";
import { ReadFailed } from "../../components/Loaded";
import { GridTable } from "../../components/ui/GridTable";
import type { GridColumn } from "../../components/ui/GridTable";
import { EmptyState } from "../../components/ui/EmptyState";
import { Tile, TileStrip } from "../../components/ui/Tile";
import { instantDate, shortDate, shortHash } from "../../lib/format";
import { useReader } from "../../api/profile";
import { amount } from "../reportsFormat";
import { isOwed } from "../reportsModel";
import { reissueWords } from "./reissue";
import { verdictWord } from "./words";
import styles from "./Filings.module.css";

interface Past {
  artifact: Artifact;
  filing: Obligation;
}

export function PastFilings({ filings }: { filings: readonly Obligation[] }) {
  const reader = useReader();
  const reads = useQueries({
    queries: filings.map((filing) => ({
      queryKey: ["artifacts", filing.obligation_id] as const,
      queryFn: async (): Promise<Artifact[]> => {
        const { data, error, response } = await api.GET(
          "/api/v1/reports/obligations/{obligation_id}/artifacts",
          { params: { path: { obligation_id: filing.obligation_id } } },
        );
        if (error) throw refusalFrom(response, error, "The filings issued could not be read.");
        return data ?? [];
      },
    })),
  });
  const failed = reads.find((read) => read.error);
  if (failed) {
    return (
      <ReadFailed of="The filings issued" error={failed.error} onRetry={() => void failed.refetch()} />
    );
  }
  if (reads.some((read) => read.isPending)) {
    return <EmptyState compact title="Reading past filings" icon={Archive} testId="past-reading" />;
  }
  const past: Past[] = reads
    .flatMap((read, at) => {
      const filing = filings[at];
      return filing ? (read.data ?? []).map((artifact) => ({ artifact, filing })) : [];
    })
    .sort((a, b) => b.artifact.issued_at.localeCompare(a.artifact.issued_at));

  const columns: GridColumn<Past>[] = [
    {
      id: "filing",
      header: "Filing",
      cell: ({ filing }) => `${filing.regime_name}, ${filing.legal_entity_name}`,
    },
    {
      id: "year",
      header: "Year end",
      width: "104px",
      cell: ({ filing }) => shortDate(filing.financial_year_end),
    },
    {
      id: "issued",
      header: "Issued",
      width: "112px",
      cell: ({ artifact }) => instantDate(artifact.issued_at, reader),
    },
    {
      id: "version",
      header: "Version",
      width: "164px",
      // A restatement says what it restates (G-Wauditor-11): version 2 of 2 looked the same as version
      // 1 of 2 and nothing said which came later. The link is the server's (`supersedes`).
      cell: ({ artifact }) =>
        `${String(artifact.version)} of ${String(artifact.of_versions)}${
          artifact.supersedes ? ` · restates v${String(artifact.version - 1)}` : ""
        }`,
    },
    {
      id: "why",
      header: "Why issued again",
      minWidth: "200px",
      wrap: true,
      // D-17 (ADR-0228): a version after the first says why, in the issuer's words, and says when it
      // followed a reopen. A version issued before reasons were asked for says so rather than
      // printing nothing, and a reader who may not read reasons is told one exists (never its words).
      cell: ({ artifact }) => reissueWords(artifact),
    },
    {
      id: "total",
      header: "Stated total",
      width: "124px",
      numeric: true,
      // Read out of the artifact, as filed: the difference between two versions is on the page.
      cell: ({ artifact }) => `${amount(artifact.stated_total_tonnes, "tCO2e")} tCO₂e`,
    },
    {
      id: "hash",
      header: "Fingerprint",
      width: "140px",
      cell: ({ artifact }) => shortHash(artifact.sha256),
    },
    {
      id: "state",
      header: "Checks said",
      width: "104px",
      cell: ({ artifact }) => verdictWord(artifact.checks_verdict).word,
    },
    {
      id: "open",
      header: "Open",
      width: "64px",
      cell: ({ artifact }) => (
        <a
          className={styles.fix}
          href={apiUrl(`/api/v1/artifacts/${artifact.id}/view`)}
          target="_blank"
          rel="noreferrer"
          data-testid="past-filing-open"
        >
          Open
        </a>
      ),
    },
  ];

  return (
    <section className={styles.past} aria-label="Past filings" data-testid="past-filings">
      <TileStrip label="Summary of past filings">
        {/* One definition of each, here and on the Engagement view (G-Wauditor-03): a filing is issued when
            at least one version of it is, so two versions of one filing are one filing issued; and a
            filing is owed when the company must file it under a law that is operative - an enjoined,
            undated one is shown, and is not counted. The versions are the rows below. */}
        <Tile
          label="Filings issued"
          value={String(new Set(past.map(({ filing }) => filing.obligation_id)).size)}
          testId="tile-past-issued"
        />
        <Tile
          label="Latest issued"
          value={past[0] ? instantDate(past[0].artifact.issued_at, reader) : "–"}
          testId="tile-past-latest"
        />
        <Tile
          label="Filings owed"
          value={String(filings.filter((filing) => isOwed(filing) && !filing.in_force_caveat).length)}
          testId="tile-past-owed"
        />
      </TileStrip>
      <GridTable
        caption="Past filings"
        columns={columns}
        rows={past}
        rowKey={({ artifact }) => artifact.id}
        rowNumbers={false}
        testId="past-filings-table"
        empty={<EmptyState compact title="Nothing has been issued yet" icon={Archive} testId="past-empty" />}
      />
    </section>
  );
}
